import { useLayoutEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function splitTitleToSpans(titleEl) {
  const text = titleEl.textContent || '';
  titleEl.textContent = '';
  const chars = [];
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const span = document.createElement('span');
    span.className = 'inline-block will-change-transform';
    span.textContent = ch === ' ' ? '\u00A0' : ch;
    titleEl.appendChild(span);
    chars.push(span);
  }
  return chars;
}

/**
 * GSAP motion for the landing hero + header (respects prefers-reduced-motion).
 * Intro + ScrollTrigger run once after mount; hero copy re-animates when `heroSlide` changes.
 */
export function useHomepageAnimations({
  reducedMotion,
  heroSlide,
  headerEl,
  brandEl,
  headerToolsEl,
  navListEl,
  logoImgEl,
  heroSectionEl,
  heroBgEl,
  heroGradientEl,
  heroDecorEl,
  heroCopyRootEl,
  heroHighlightsEl,
  heroTabsEl,
}) {
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    setReady(true);
  }, []);

  useLayoutEffect(() => {
    if (reducedMotion || !ready) return undefined;

    const h = headerEl?.current;
    const brand = brandEl?.current;
    const tools = headerToolsEl?.current;
    const nav = navListEl?.current;
    const heroSec = heroSectionEl?.current;
    const bg = heroBgEl?.current;
    const grad = heroGradientEl?.current;
    const decor = heroDecorEl?.current;
    const highlights = heroHighlightsEl?.current;
    const tabs = heroTabsEl?.current;
    const logo = logoImgEl?.current;

    if (!h || !heroSec) return undefined;

    const ctx = gsap.context(() => {
      if (brand && tools && nav) {
        const navItems = nav.querySelectorAll(':scope > li');
        const tl = gsap.timeline({
          defaults: { ease: 'power3.out' },
          onComplete: () => {
            /* Let CSS control header translate when sticky (slide-down bar). */
            gsap.set(h, { clearProps: 'transform' });
          },
        });
        tl.from(h, { autoAlpha: 0, y: -36, duration: 0.88 }, 0);
        tl.from(brand, { autoAlpha: 0, x: -24, duration: 0.64 }, 0.1);
        tl.from(navItems, { autoAlpha: 0, y: -14, stagger: 0.056, duration: 0.52 }, 0.16);
        tl.from(tools, { autoAlpha: 0, x: 22, duration: 0.6 }, 0.12);
      }

      if (bg) {
        gsap.to(bg, {
          yPercent: 11,
          ease: 'none',
          scrollTrigger: {
            trigger: heroSec,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        });
      }

      if (grad) {
        gsap.fromTo(
          grad,
          { opacity: 0.82 },
          {
            opacity: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: heroSec,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          }
        );
      }

      if (logo) {
        gsap.fromTo(
          logo,
          { scale: 1 },
          {
            scale: 0.9,
            ease: 'none',
            scrollTrigger: {
              trigger: heroSec,
              start: 'top top',
              end: '+=260',
              scrub: 0.55,
            },
          }
        );
      }

      if (decor && decor.children.length) {
        gsap.to(decor.children, {
          y: (i) => (i % 2 === 0 ? 20 : -16),
          x: (i) => (i % 2 === 0 ? -12 : 14),
          duration: (i) => 4.2 + i * 0.75,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          stagger: { each: 0.35 },
        });
      }

      if (highlights) {
        gsap.from(highlights.querySelectorAll(':scope > li'), {
          autoAlpha: 0,
          y: 40,
          duration: 0.78,
          stagger: 0.12,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: highlights,
            start: 'top 90%',
            toggleActions: 'play none none none',
          },
        });
      }

      if (tabs) {
        gsap.from(tabs, {
          autoAlpha: 0,
          y: 14,
          duration: 0.58,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: tabs,
            start: 'top 93%',
            toggleActions: 'play none none none',
          },
        });
      }

      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });
    });

    return () => {
      ctx.revert();
    };
  }, [reducedMotion, ready, headerEl, brandEl, headerToolsEl, navListEl, logoImgEl, heroSectionEl, heroBgEl, heroGradientEl, heroDecorEl, heroHighlightsEl, heroTabsEl]);

  useLayoutEffect(() => {
    if (reducedMotion || !ready) return undefined;
    const root = heroCopyRootEl?.current;
    if (!root) return undefined;

    const ctx = gsap.context(() => {
      const labelEl = root.querySelector('[data-hero-label]');
      const titleEl = root.querySelector('[data-hero-title]');
      const subtitleEl = root.querySelector('[data-hero-subtitle]');
      const ctaEl = root.querySelector('[data-hero-cta]');

      if (labelEl) {
        gsap.from(labelEl, {
          autoAlpha: 0,
          y: 18,
          duration: 0.62,
          ease: 'power3.out',
          delay: 0.05,
        });
      }

      if (titleEl) {
        const h1 = titleEl.closest('h1');
        if (h1) {
          gsap.set(h1, { perspective: 920, transformStyle: 'preserve-3d' });
        }
        const chars = splitTitleToSpans(titleEl);
        gsap.from(chars, {
          yPercent: 115,
          rotateX: -50,
          autoAlpha: 0,
          stagger: 0.019,
          duration: 0.72,
          ease: 'power3.out',
          delay: 0.12,
        });
      }

      if (subtitleEl) {
        gsap.from(subtitleEl, {
          autoAlpha: 0,
          y: 22,
          duration: 0.78,
          ease: 'power3.out',
          delay: 0.52,
        });
      }

      if (ctaEl) {
        gsap.from(ctaEl, {
          autoAlpha: 0,
          scale: 0.9,
          y: 20,
          duration: 0.68,
          ease: 'back.out(1.35)',
          delay: 0.72,
        });
      }
    }, root);

    return () => {
      ctx.revert();
    };
  }, [reducedMotion, ready, heroSlide, heroCopyRootEl]);
}
